import React, { memo, useRef, useState, useCallback, useEffect, useMemo } from "react";
import { FixedSizeList, areEqual } from "react-window";
import { Droppable, Draggable, DragDropContext } from "@hello-pangea/dnd";
import withAutoSize from "../helpers/withAutoSize";

const emptyObject = {};

function getStyle({ draggableProps }, snapshot, source) {
  if (!draggableProps || !draggableProps.style) return source.style;

  return {
    ...draggableProps.style,
    ...source.style,
  };
}

function VirtuosoTree({
  dataKey = "",
  className = "",
  item, //required, (index, provided, snapshot)
  totalCount = 0,
  footer, //incomplete!
  itemHeight = 0,
  scrollToIndex = -1,
  rowIsDraggable, //func, optional (index)
  rowIsDroppable, //func, optional (fromIndex, toIndex)
  onDragStart, //func, optional (index)
  onDragEnd, //func (fromIndex,toIndex,action=combine|move)
  onLongHover, //func, optional (fromIndex,toIndex)
  height,
}) {
  const _list = useRef(null);
  const _longHover = useRef();

  const [isCombineEnabled, setIsCombineEnabled] = useState(
    () => (rowIsDroppable ? true : false)
  );

  const latest = useRef({ scrollToIndex, rowIsDroppable, onLongHover, onDragStart, onDragEnd, item });
  latest.current = { scrollToIndex, rowIsDroppable, onLongHover, onDragStart, onDragEnd, item };

  const scrollTo = useCallback(() => {
    const { scrollToIndex } = latest.current;
    if (scrollToIndex < 0 || !_list.current) return;

    _list.current.scrollToItem(scrollToIndex);
  }, []);

  useEffect(() => {
    scrollTo();
  }, [scrollToIndex, scrollTo]);

  const onDragStartCb = useCallback(({ source }) => {
    latest.current.onDragStart && latest.current.onDragStart(source.index);
  }, []);

  const onDragUpdate = useCallback(({ source, combine }) => {
    clearTimeout(_longHover.current);

    //Enable or disable dropping into items
    let enabled = latest.current.rowIsDroppable ? true : false;
    if (combine && latest.current.rowIsDroppable) {
      const to = Number.parseInt(combine.draggableId);
      enabled = latest.current.rowIsDroppable(source.index, to);

      //Long hover
      if (latest.current.onLongHover && enabled)
        _longHover.current = setTimeout(() => {
          latest.current.onLongHover(source.index, to);
        }, 500);
    }

    setIsCombineEnabled(enabled);
  }, []);

  const onDragEndCb = useCallback(({ source, destination, combine }) => {
    clearTimeout(_longHover.current);

    if (!latest.current.onDragEnd) return;

    if (combine)
      return latest.current.onDragEnd(
        source.index,
        Number.parseInt(combine.draggableId),
        "combine"
      );
    else if (destination && source.index != destination.index)
      latest.current.onDragEnd(source.index, destination.index, "move");
  }, []);

  const renderClone = useCallback(
    (provided = emptyObject, snapshot = emptyObject, { source }) => (
      <div
        key={source.index}
        ref={provided.innerRef}
        {...(provided.draggableProps || {})}
        {...(provided.dragHandleProps || {})}
        tabIndex={-1}
        style={getStyle(provided, snapshot, source)}
        onClick={null}
      >
        {latest.current.item(source.index, provided, snapshot)}
      </div>
    ),
    []
  );

  const renderRow = useMemo(
    () =>
      memo(
        source => {
          //drag disabled
          if (latest.current.rowIsDraggable && !latest.current.rowIsDraggable(source.index))
            return renderClone(undefined, undefined, { source });

          //drag enabled
          return (
            <Draggable
              key={source.index}
              draggableId={String(source.index)}
              index={source.index}
            >
              {(provided, snapshot) =>
                renderClone(provided, snapshot, { source })
              }
            </Draggable>
          );
        },
        areEqual
      ),
    [renderClone]
  );

  return (
    <DragDropContext
      onDragStart={onDragStartCb}
      onDragUpdate={onDragUpdate}
      onDragEnd={onDragEndCb}
    >
      <Droppable
        droppableId="default"
        mode="virtual"
        isCombineEnabled={isCombineEnabled}
        renderClone={renderClone}
      >
        {({ innerRef }) => (
          <FixedSizeList
            ref={_list}
            outerRef={innerRef}
            outerElementType={OuterElement}
            className={"ui-virtual-tree " + className}
            width="100%"
            height={height}
            itemCount={totalCount}
            itemSize={itemHeight}
            itemData={dataKey}
            overscanCount={10}
          >
            {renderRow}
          </FixedSizeList>
        )}
      </Droppable>
    </DragDropContext>
  );
}

const OuterElement = React.forwardRef((props, ref) => (
  <div ref={ref} tabIndex={-1} {...props} />
));

export default withAutoSize(VirtuosoTree, "tree");
